import { debounce } from 'lodash';
import { parse } from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { v4 as uuid } from 'uuid';
import CopyToClipboardLink from '../../../components/CopyToClipboardLink';
import Loader from '../../../components/Loader';
import PageStatus from '../../../components/PageStatus';
import { ROUTES } from '../../../routes/routes';
import {
    BorderRadius,
    CustomProduct,
    FeatureIcon,
    PageDesignSettings,
    PageSettings,
    Plan,
    PricingPage,
    Product,
    SubduedIcon,
} from '../../../types/pricingPage';
import { StripePrice } from '../../../types/stripePrice';
import { DEFAULT_PRICING_CUSTOM_CSS } from '../../../utils/constants';
import { useAxios } from '../../../utils/hooks';
import { getStripeMode, toPlan, toPlans } from '../../../utils/products';
import ChooseDesign from './ChooseDesign';
import ChooseFeatures from './ChooseFeatures';
import ChooseFlow from './ChooseFlow';
import ChooseProducts from './ChooseProducts';
import CopySnippet from './CopySnippet';
import Preview from './Preview';
import ProductsHint from './ProductsHint';
import Header from '../../../components/Header';

const getDefaultSettings = (): PageSettings => ({
    id: uuid(),
    monthlyYearlyToggle: false,
    displayMonthlyPrice: false,
    promotedFrequency: false,
    promotedFrequencyWording: 'Save 20%',
    promotedPlan: true,
    customWording: {},
    planPicker: {
        enabled: false,
        proration: false,
        disableOneOff: false,
        disableZeroPrice: false,
        confirmChanges: false,
        scheduleUpgrades: false,
        scheduleDowngrades: false,
    },
});

const getDefaultDesignSettings = (): PageDesignSettings => ({
    id: uuid(),
    backgroundColor: '#2953AF',
    buttonGradient: true,
    borderRadius: BorderRadius.LARGE,
    featureIcon: FeatureIcon.GREEN,
    subduedIcon: SubduedIcon.GREY_X,
    shadow: 'none',
    animatePlanOnHover: false,
    disableFlexCol: false,
    customCSS: DEFAULT_PRICING_CUSTOM_CSS,
    companyName: '',
    mostPopular: { enabled: true, wording: 'Popular' },
    layout: { button: 'bottom' },
    hideProductImages: false,
});

export interface PageWizardProps {
    allProducts: Product[];
    selectedProducts: Product[];
    page: PricingPage;
}

export default function NewPage() {
    const { addToast } = useToasts();
    const axios = useAxios();
    const { search } = useLocation();
    const [page, setPage] = useState<PricingPage>({
        id: uuid(),
        name: new Date().toLocaleDateString(),
        settings: getDefaultSettings(),
        plans: [],
        designSettings: getDefaultDesignSettings(),
        status: 'DRAFT',
        secureMode: false,
    });
    const [stripeProducts, setProducts] = useState<Product[]>();
    const [customProducts, setCustomProducts] = useState<Product[]>();
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

    const loadProducts = useCallback(
        (status: PricingPage['status']) => {
            if (!axios) {
                return;
            }
            setProducts(undefined);
            axios.current
                ?.get<StripePrice[]>(`/stripe/products/${getStripeMode(status)}`)
                .then(({ data }) => {
                    setProducts(data);
                })
                .catch((err) => {
                    if (err?.response?.status !== 400) {
                        addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                    } else {
                        setProducts([]);
                    }
                });
        },
        [axios, addToast]
    );

    const loadPage = useCallback(() => {
        if (!axios) {
            return;
        }
        const { pageId } = parse(search);
        if (pageId) {
            axios.current
                ?.get<PricingPage>(`/pricing-pages/${pageId}`)
                .then((res) => {
                    const page = res.data;
                    setPage(page);
                    loadProducts(page.status);
                    setSelectedProducts(page.plans.filter((plan) => !!plan.product).map((plan) => plan.product));
                    const customProducts = page.plans
                        .filter((plan) => plan.customProduct)
                        .map((plan) => plan.customProduct as CustomProduct);
                    setCustomProducts(customProducts || []);
                })
                .catch((err) => {
                    console.log(err);
                    addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                });
        } else {
            loadProducts(page.status);
            setCustomProducts([]);
        }
    }, [axios, search, loadProducts, addToast, page.status]);

    useEffect(() => {
        if (!axios || stripeProducts || customProducts) {
            return;
        }

        loadPage();
    }, [axios, loadPage, stripeProducts, customProducts]);

    const handleUpdateSelectedProducts = async (newSelectedProducts: Product[]) => {
        setSelectedProducts(newSelectedProducts);
        const existingPaymentFlow = page.plans.length > 0 ? page.plans[0].paymentFlow : undefined;
        const newPlans: Plan[] = newSelectedProducts.map((product, idx) => {
            const existingPlan = page.plans.find(
                (plan) => plan.stripePriceId === product.id || plan?.customProduct?.id === product.id
            );
            const updatedPlan = existingPlan
                ? { ...existingPlan, order: idx, paypalPlanId: product.paypalPlanId }
                : toPlan(product, idx, existingPaymentFlow);
            return updatedPlan;
        });
        const newPage = { ...page, plans: newPlans };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
    };

    const handleUpdateSettings = async (settings: PageSettings) => {
        const newPage = { ...page, settings };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
    };

    const handleUpdatePlans = async (plans: Plan[]) => {
        const newPage = { ...page, plans };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
    };

    const handleUpdateDesignSettings = async (designSettings: PageDesignSettings) => {
        const newPage = { ...page, designSettings };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
    };

    const handleChangeName = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.currentTarget.value;
        const newPage = { ...page, name };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
    };

    const handleUpdateStatus = async (status: PricingPage['status']) => {
        const newPage = { ...page, status };
        setPage(newPage);
        sendPageUpdateRequest(newPage);
        loadProducts(status);
    };

    const handleAddCustomProduct = (product: Product) => setCustomProducts([...(customProducts || []), product]);
    const handleDeleteCustomProduct = (productToDelete: CustomProduct) =>
        setCustomProducts((customProducts || []).filter((product) => product.id !== productToDelete.id));

    const sendPageUpdateRequest = useCallback(
        debounce(
            async (newPage: PricingPage) =>
                axios.current
                    ?.post('/pricing-pages', newPage)
                    .catch((err) =>
                        addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' })
                    ),
            1000
        ),
        [axios, page, addToast]
    );

    const plansWithProducts = useMemo<Plan[]>(
        () => (page.plans.length > 0 ? page.plans : toPlans(selectedProducts)),
        [page.plans, selectedProducts]
    );

    const products = useMemo(
        () =>
            stripeProducts && customProducts
                ? [...stripeProducts, ...customProducts].sort((productA, productB) =>
                      (page.plans.find(
                          (plan) => plan.customProduct?.id === productA.id || plan.product?.id === productA.id
                      )?.order ?? 0) >
                      (page.plans.find(
                          (plan) => plan.customProduct?.id === productB.id || plan.product?.id === productB.id
                      )?.order ?? 0)
                          ? 1
                          : -1
                  )
                : undefined,
        [stripeProducts, customProducts, page.plans]
    );

    const ChooseProductsPage = () => {
        if (!products) {
            return null;
        }
        return (
            <ChooseProducts
                allProducts={products}
                selectedProducts={selectedProducts}
                page={page}
                onReloadProducts={() => loadProducts(page.status)}
                onChangeSelectedProducts={handleUpdateSelectedProducts}
                onAddCustomProduct={handleAddCustomProduct}
                onDeleteCustomProduct={handleDeleteCustomProduct}
                onChangeMode={handleUpdateStatus}
            />
        );
    };

    const ChooseFlowPage = () => {
        if (!products) {
            return null;
        }
        return (
            <ChooseFlow
                allProducts={products}
                selectedProducts={selectedProducts}
                page={page}
                onChangePlans={handleUpdatePlans}
            />
        );
    };

    const ChooseFeaturesPage = () => {
        if (!products) {
            return null;
        }
        return (
            <ChooseFeatures
                allProducts={products}
                selectedProducts={selectedProducts}
                page={page}
                onChangePlans={handleUpdatePlans}
            />
        );
    };

    const ChooseDesignPage = () => {
        if (!products) {
            return null;
        }
        return (
            <ChooseDesign
                allProducts={products}
                selectedProducts={selectedProducts}
                page={page}
                onChangeDesignSettings={handleUpdateDesignSettings}
                onChangeSettings={handleUpdateSettings}
            />
        );
    };

    const SnippetPage = () => {
        if (!products) {
            return null;
        }

        return <CopySnippet allProducts={products} selectedProducts={selectedProducts} page={page} />;
    };

    const ProductHintPanel = () => {
        if (!products) {
            return null;
        }

        return <ProductsHint allProducts={products} onChangeMode={handleUpdateStatus} status={page.status} />;
    };

    if (typeof products === 'undefined') {
        return (
            <div className="w-full">
                <Loader full />
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 p-4">
            <div className="order-last lg:order-none">
                <Switch>
                    <Route exact path={ROUTES.PAGES_NEW} render={ChooseProductsPage} />
                    <Route exact path={ROUTES.PAGES_NEW_FLOW} render={ChooseFlowPage} />
                    <Route exact path={ROUTES.PAGES_NEW_FEATURES} render={ChooseFeaturesPage} />
                    <Route exact path={ROUTES.PAGES_NEW_DESIGN} render={ChooseDesignPage} />
                    <Route exact path={ROUTES.PAGES_NEW_SNIPPET} render={SnippetPage} />
                </Switch>
            </div>
            {products && (
                <div className="grid grid-cols-1">
                    <div className="relative border-b sm:border-0 px-4 overflow-scroll">
                        <div className="flex flex-col md:flex-row space-y-2 static sm:sticky bg-gray-100 top-0 z-10">
                            <div>
                                <label className="block text-sm text-gray-600" htmlFor="pageName">
                                    Name
                                </label>
                                <input
                                    id="pageName"
                                    type="text"
                                    value={page.name}
                                    onChange={handleChangeName}
                                    className="input bg-gray-200 border border-gray-200 focus:outline-none focus:bg-white focus:border-gray-500"
                                    placeholder="Page name..."
                                />
                            </div>
                            <Header className="mx-auto">Preview</Header>
                            <div className="mr-0 ml-auto flex space-x-4">
                                <CopyToClipboardLink name="Page ID" value={page.id} />
                                <PageStatus page={page} liveWarning onChangeStatus={handleUpdateStatus} />
                            </div>
                        </div>
                        <Preview page={{ ...page, plans: plansWithProducts }} zoom={75} />
                        <Switch>
                            <Route exact path={ROUTES.PAGES_NEW} render={ProductHintPanel} />
                        </Switch>
                    </div>
                </div>
            )}
        </div>
    );
}
